// src/econ/baseline.ts — the cold-start economy the scenario perturbs.
//
// Mirrors model/seed.ts: committed approximations, each flagged ESTIMATE until a
// live ingest (src/ingest/labor.ts) upgrades the FRED-sourced fields to LIVE (or
// STALE). knowledge_worker_share is DERIVED here from the committed OEWS +
// knowledge-occupation mapping so the derivation is deterministic and testable —
// it stays ESTIMATE because the mapping is a judgement call (plan §5).

import knowledgeOcc from "../../data/econ/knowledge_occupations.json";
import oews from "../../data/econ/oews_baseline.json";
import type { EconBaseline, Field } from "./types.ts";

/**
 * Knowledge-work share of employment = Σ(group employment × knowledge weight)
 * / Σ(group employment), over the committed OEWS major-group totals. Pure and
 * deterministic; the same inputs always give the same share.
 */
export function deriveKnowledgeShare(): number {
  const emp = oews.employment_by_soc_major as Record<string, number>;
  const weights = knowledgeOcc.weights as Record<string, { weight: number }>;
  let total = 0;
  let knowledge = 0;
  for (const [soc, headcount] of Object.entries(emp)) {
    total += headcount;
    const w = weights[soc]?.weight ?? 0;
    knowledge += headcount * w;
  }
  return total > 0 ? knowledge / total : 0;
}

/** Anchor: labor share (0..1) at labor-share index = 100. Documented ESTIMATE. */
export const LABOR_SHARE_ANCHOR = 0.585;

const f = (value: number, source: string, as_of: string, note?: string): Field => ({
  value,
  flag: "ESTIMATE",
  source,
  as_of,
  note,
});

/**
 * The cold-start baseline (approximate current US values). The app is fully
 * functional with zero live data; ingest upgrades these in place.
 */
export function coldStartBaseline(): EconBaseline {
  return {
    gdp: f(23500, "FRED:GDPC1", "2026-06-30", "Real GDP, $B chained 2017, annualized."),
    unemployment: f(0.043, "FRED:UNRATE", "2026-08-01", "Civilian unemployment rate."),
    labor_share: f(
      LABOR_SHARE_ANCHOR,
      "FRED:PRS85006173",
      "2026-06-30",
      "Nonfarm business labor share. Level anchored to " + LABOR_SHARE_ANCHOR + " at index=100; movement is live.",
    ),
    employment: f(159500, "FRED:PAYEMS", "2026-08-01", "Total nonfarm employment, thousands."),
    knowledge_worker_share: f(
      deriveKnowledgeShare(),
      "OEWS+mapping",
      String(oews.as_of),
      "Derived from OEWS major-group employment × a documented knowledge-work mapping. A judgement call.",
    ),
    openings: f(7200, "FRED:JTSJOL", "2026-07-01", "Job openings, thousands (JOLTS)."),
    hires: f(5300, "FRED:JTSHIL", "2026-07-01", "Hires, thousands (JOLTS)."),
    separations: f(5100, "FRED:JTSTSL", "2026-07-01", "Total separations, thousands (JOLTS)."),
  };
}
